import React from 'react';

import { CheckOutlined, CloseOutlined, EditOutlined, RetweetOutlined, WarningOutlined } from '@ant-design/icons';

import { Button, Flex, Layout, Row, Space, Spin, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { NSKeys } from '@app/i18n';
import {
  ActionKind,
  ArtifactItemSchema,
  OrderItemsArtifactSchema,
  ReserveStateSchema,
  WarehouseFlowSchema,
} from '@app/models/orders';
import { hasHistory } from '@app/services/hasHistory';

import { Breadcrumbs } from '@components/Breadcrumbs';
import { HeaderTitle } from '@components/HeaderTitle';

import { OrderTabs } from '@components/OrderTabs';
import { OrderTab } from '@components/OrderTabs/interfaces';
import { Paper } from '@components/Paper';

import { useApplyActionQuery, useGetOrderQuery, useGetProcessQuery } from '../../../../hooks';

import { ChangeProject } from '../../components/ChangeProject';
import { ChangeStatus } from '../../components/ChangeStatus';

import { HistoryTable } from './components/HistoryTable';
import { OrderArtifact } from './components/OrderArtifact';
import { OrderFieldGroup } from './components/OrderFieldGroup';
import { StatusChangeModal } from './components/StatusChangeModal';
import { WarehouseStatusBlock } from './components/WarehouseStatusBlock';

export const Single: React.FC = () => {
  const itemsTableRef = React.useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const orderT = useTranslation(NSKeys.order);
  const commonT = useTranslation(NSKeys.common);
  const params = useParams<{ orderId: string; processId: string }>();
  const { data: orderData, isLoading: orderIsLoading } = useGetOrderQuery(params?.orderId || '');
  const { data: processData, isLoading: isProcessLoading } = useGetProcessQuery(params?.processId || '');
  const { mutate: applyAction, status: applyStatus, isLoading: isApplyingAction } = useApplyActionQuery();

  const isLoading = orderIsLoading || isProcessLoading;

  const reserveArtifactUid = processData?.order_structure?.items?.find(
    (item): item is ArtifactItemSchema => item.type === 'artifact' && item.data.type === 'reserve_state'
  )?.data?.uid;
  const reserveState: ReserveStateSchema | null | undefined = reserveArtifactUid
    ? (orderData?.artifacts as Record<string, any> | null | undefined)?.[reserveArtifactUid]
    : undefined;
  const warehouseFlowArtifactUid = processData?.order_structure?.items?.find(
    (item): item is ArtifactItemSchema => item.type === 'artifact' && item.data.type === 'warehouse_flow'
  )?.data?.uid;
  const warehouseFlow: WarehouseFlowSchema | null | undefined = warehouseFlowArtifactUid
    ? (orderData?.artifacts as Record<string, any> | null | undefined)?.[warehouseFlowArtifactUid]
    : undefined;
  const warehouseFlowView = warehouseFlowArtifactUid ? orderData?.$artifacts?.[warehouseFlowArtifactUid]?.view : false;
  const orderItemsArtifact = processData?.order_structure?.items?.find(
    (item): item is ArtifactItemSchema => item.type === 'artifact' && item.data.type === 'order_items'
  );
  const orderItemsView = orderItemsArtifact ? orderData?.$artifacts?.[orderItemsArtifact.data.uid]?.view : false;
  const orderItemsData: OrderItemsArtifactSchema | null | undefined = orderItemsArtifact
    ? (orderData?.artifacts as Record<string, any> | null | undefined)?.[orderItemsArtifact.data.uid]
    : undefined;

  const goBack = () => (hasHistory() ? navigate(-1) : navigate(`/dashboard/processes/${params.processId}/orders`));

  return (
    <Layout>
      <Layout.Header style={{ borderBottom: 'none' }}>
        <Breadcrumbs apiTitles={[{ id: ':ordersId', value: `${orderT.t('breadCrumbPrefix')} ${orderData?.num}` }]} />
        <Row justify="space-between" align="middle" style={{ minHeight: 64 }}>
          <Flex gap={24}>
            <HeaderTitle
              loading={isLoading}
              title={`${orderData?.num || params.orderId}`}
              onClick={goBack}></HeaderTitle>
            {orderData && params.orderId && (
              <Space wrap>
                {orderData.$actions.some((el) => el === ActionKind.ApproveOrder) && (
                  <>
                    <Button
                      icon={<CheckOutlined />}
                      loading={isApplyingAction}
                      onClick={() =>
                        applyAction({
                          order_uid: params.orderId || '',
                          kind: ActionKind.ApproveOrder,
                          data: { payload: { version: orderData?.version } },
                        })
                      }
                      size="middle">
                      {commonT.t('buttonApprove')}
                    </Button>
                  </>
                )}

                {orderData.$actions.some((el) => el === ActionKind.ApprovalCancelOrder) && (
                  <StatusChangeModal
                    isLoading={isApplyingAction}
                    onSubmit={(data) =>
                      applyAction({
                        order_uid: params.orderId || '',
                        kind: ActionKind.ApprovalCancelOrder,
                        data: { payload: { comment: data.comment, version: orderData?.version } },
                      })
                    }
                    status={applyStatus}
                    buttonContent={commonT.t('buttonCancel')}
                    buttonIcon={<CloseOutlined />}
                  />
                )}

                {orderData.$actions.some((el) => el === ActionKind.MarkAsNeedsWork) && (
                  <StatusChangeModal
                    isLoading={isApplyingAction}
                    onSubmit={(data) =>
                      applyAction({
                        order_uid: params.orderId || '',
                        kind: ActionKind.MarkAsNeedsWork,
                        data: { payload: { comment: data.comment, version: orderData?.version } },
                      })
                    }
                    status={applyStatus}
                    buttonContent={orderT.t('buttonMarkAsNeedWork')}
                    buttonIcon={<RetweetOutlined />}
                  />
                )}
                {orderItemsView &&
                  orderItemsData?.show_multiplicity_indicator === true &&
                  orderItemsData.has_not_multiple_items === true && (
                    <Tooltip title={orderT.t('multiplicityWarning')}>
                      <Button
                        aria-label={orderT.t('multiplicityWarning')}
                        color="orange"
                        variant="filled"
                        icon={<WarningOutlined style={{ color: '#faad14' }} />}
                        onClick={() => {
                          setSearchParams((current) => {
                            const next = new URLSearchParams(current);
                            next.set('tab', OrderTab.Details);
                            return next;
                          });
                          window.requestAnimationFrame(() =>
                            itemsTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                          );
                        }}
                        shape="circle"
                        size="middle"
                      />
                    </Tooltip>
                  )}
                <ChangeStatus
                  version={orderData.version}
                  processData={processData}
                  processLoading={isProcessLoading}
                  moveAvailable={!!orderData?.$permissions?.move_to_status}
                  statusUid={orderData?.current_status_uid}
                  orderUid={params.orderId}
                  available_statuses_uids={orderData?.$available_statuses?.map((el) => el.uid)}
                />
              </Space>
            )}
            {params?.processId && orderData?.uid && (
              <ChangeProject
                permissionMoveToProject={orderData?.$permissions?.move_to_project || false}
                process_uid={params?.processId}
                order_uid={orderData.uid}
                project_uid={orderData?.project?.uid}
                project_label={orderData?.project?.title}
              />
            )}
          </Flex>
          <Space>
            {orderData?.$operations?.update && orderData?.$permissions?.update && (
              <Button
                icon={<EditOutlined />}
                onClick={() => navigate(`/dashboard/processes/${params.processId}/orders/edit/${params?.orderId}`)}
                size="middle">
                {commonT.t('edit')}
              </Button>
            )}
          </Space>
        </Row>
      </Layout.Header>
      {isLoading ? (
        <Spin style={{ padding: 24 }} />
      ) : (
        <OrderTabs orderUid={orderData?.uid || ''}>
          <Flex vertical={true} gap={0} style={{ padding: 24 }}>
            <WarehouseStatusBlock
              reserveState={reserveState}
              warehouseFlow={warehouseFlow}
              warehouseFlowView={warehouseFlowView}
            />

            {processData?.order_structure?.items?.map((item, i) =>
              item.type === 'field_group' ? (
                <OrderFieldGroup key={`item${i}`} item={item} isLoading={isLoading} orderData={orderData} />
              ) : item.type === 'artifact' && item.data.type === 'order_items' ? (
                <div key={`item${i}`} ref={itemsTableRef}>
                  <OrderArtifact artifact={item} orderData={orderData} />
                </div>
              ) : item.type === 'artifact' ? (
                <OrderArtifact key={`item${i}`} artifact={item} orderData={orderData} />
              ) : null
            )}

            <Paper>
              {params.orderId && (
                <HistoryTable
                  order_uid={params.orderId}
                  statuses={
                    processData?.stages
                      ?.map((stage) => stage.statuses)
                      ?.flat()
                      ?.map((el) => ({ uid: el.uid, name: el.name || el.alias, color: el.color })) as any
                  }
                />
              )}
            </Paper>
          </Flex>
        </OrderTabs>
      )}
    </Layout>
  );
};
